import React, { useEffect, useMemo, useState } from 'react';
import { sheetsClient } from '../../data/sheetsClient';
import { useTeacherData } from '../../context/TeacherDataContext';
import { Card, Button, Skeleton } from '../shared/UI';
import { DANCER_ROLES } from '../../../shared/entryOptions';

const MAX_WORDS = 100;

const QUALITIES = [
  'Respect for the Art Form, the Artists and the Creative Space',
  'Creative',
  'Enthusiastic',
  'Dedicated',
  'Encouraging to other dancers',
  'Listens to others',
  'Speaks thoughtfully',
  'Works well with other dancers during the creative process/rehearsals',
  'Demonstrates leadership',
  'Punctual for rehearsals',
  'Works to deadlines',
  'Always provides support to the teacher and/or choreographer',
  'A great role model for others',
  '“Exceeds the expectations”',
];

const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #D5D5D5', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' };
const labelStyle = { fontSize: 13, color: '#666', marginBottom: 5, display: 'block' };

function countWords(text) {
  return String(text || '').trim().split(/\s+/).filter(Boolean).length;
}

// Each school nominates ONE attending dancer as its Most Inspiring Dancer,
// announced at the banquet. Reached from the "Most Inspiring Dancer Award"
// timeline row. Editable until that row's date passes (enforced server-side
// too — see PUT /api/teacher/inspiring-dancer).
export default function InspiringDancer({ navigateTo }) {
  const { school, event, dancers, loading, refetch } = useTeacherData();
  const eventYear = event?.[0]?.startDate?.match(/\d{4}/)?.[0];

  const [state, setState] = useState(null); // { nomination, deadline, locked }
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({ teacherName: '', participantId: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    sheetsClient.getInspiringDancer()
      .then((res) => {
        setState(res);
        setForm({
          teacherName: res.nomination?.teacherName || '',
          participantId: res.nomination?.participantId || '',
          reason: res.nomination?.reason || '',
        });
      })
      .catch((err) => setLoadError(err.message));
  }, []);

  // Pre-fill the teacher's name from the school account for a new nomination.
  useEffect(() => {
    if (state && !state.nomination && school?.teacherName) {
      setForm((f) => (f.teacherName ? f : { ...f, teacherName: school.teacherName }));
    }
  }, [state, school]);

  const dancerOptions = useMemo(
    () => (dancers || [])
      .filter((d) => DANCER_ROLES.includes(d.role))
      .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)),
    [dancers]
  );

  if (loadError) {
    return (
      <div style={{ padding: 20, maxWidth: 680, margin: '0 auto' }}>
        <Card><span style={{ fontSize: 13, color: '#C0392B' }}>{loadError}</span></Card>
      </div>
    );
  }

  if (!state || loading.dancers || !school) {
    return (
      <div style={{ padding: 20, maxWidth: 680, margin: '0 auto' }}>
        <Skeleton width={260} height={26} style={{ marginBottom: 10 }} />
        <Skeleton width={380} height={14} style={{ marginBottom: 20 }} />
        <Card><Skeleton height={120} /></Card>
      </div>
    );
  }

  const { nomination, deadline, locked } = state;
  const words = countWords(form.reason);
  const overLimit = words > MAX_WORDS;
  // The nominated dancer may have since been removed from the roster — keep
  // showing their saved name rather than a blank select.
  const nominatedMissing = nomination?.participantId && !dancerOptions.some((d) => d.id === nomination.participantId);
  const canSave = !locked && !saving && form.teacherName.trim() && form.participantId && form.reason.trim() && !overLimit;

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await sheetsClient.saveInspiringDancer({
        teacherName: form.teacherName.trim(),
        participantId: form.participantId,
        reason: form.reason.trim(),
      });
      const fresh = await sheetsClient.getInspiringDancer();
      setState(fresh);
      setSavedAt(new Date());
      refetch.timeline();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 680, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <Button size="sm" onClick={() => navigateTo('dashboard')}>← Back to dashboard</Button>
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>
        OSSDF{eventYear ? ` ${eventYear}` : ''} Most Inspiring Dancer
      </h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
        Select ONE of your dance students attending DANCEFEST{eventYear ? ` ${eventYear}` : ''} as the
        "Most Inspiring Dancer" for your school. The person you choose will be announced at the OSSDF Most
        Inspiring Dancer Presentation at the Banquet.
        {deadline && <> Due <strong>{deadline}</strong>.</>}
      </p>

      <Card title="Qualities of the Most Inspiring Dancer">
        <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', columns: '2 260px', columnGap: 24 }}>
          {QUALITIES.map((q) => (
            <li key={q} style={{ fontSize: 13, color: '#444', marginBottom: 6, breakInside: 'avoid', display: 'flex', gap: 6 }}>
              <span style={{ color: '#0F6E56' }}>✔</span><span>{q}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title={nomination ? 'Your nomination' : 'Nominate a dancer'}>
        {locked && (
          <div style={{ background: '#F5F5F5', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#666', marginBottom: 14 }}>
            The deadline has passed, so this nomination can no longer be changed. Contact OSSDF if you need to make a change.
          </div>
        )}
        {!locked && !nomination && (
          <div style={{ fontSize: 13, color: '#854F0B', marginBottom: 14 }}>Not submitted yet.</div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>School name</label>
          <input style={{ ...inputStyle, background: '#F5F5F5', color: '#888' }} value={school.name || ''} readOnly />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Teacher's name<span style={{ color: '#C0392B', marginLeft: 2 }}>*</span></label>
          <input style={inputStyle} value={form.teacherName} disabled={locked} maxLength={200}
            onChange={(e) => setForm({ ...form, teacherName: e.target.value })} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Most Inspiring Dancer<span style={{ color: '#C0392B', marginLeft: 2 }}>*</span></label>
          <select style={{ ...inputStyle, background: locked ? '#F5F5F5' : '#fff' }} value={form.participantId} disabled={locked}
            onChange={(e) => setForm({ ...form, participantId: e.target.value })}>
            <option value="" disabled>Select a dancer from your roster…</option>
            {nominatedMissing && <option value={nomination.participantId}>{nomination.dancerName} (no longer on roster)</option>}
            {dancerOptions.map((d) => <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>)}
          </select>
          {dancerOptions.length === 0 && (
            <span style={{ display: 'block', fontSize: 12, color: '#999', marginTop: 5 }}>No dancers on your roster yet.</span>
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>
            In 100 words or fewer, explain WHY this student is the Most Inspiring Dancer at your school
            <span style={{ color: '#C0392B', marginLeft: 2 }}>*</span>
          </label>
          <textarea style={{ ...inputStyle, height: 160, resize: 'vertical' }} value={form.reason} disabled={locked}
            onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 5, gap: 8 }}>
            <span style={{ color: '#666', fontStyle: 'italic' }}>To keep the process equitable, please do not exceed 100 words.</span>
            <span style={{ color: overLimit ? '#C0392B' : '#666', fontWeight: overLimit ? 600 : 400, whiteSpace: 'nowrap' }}>
              {words} / {MAX_WORDS} words
            </span>
          </div>
        </div>

        {saveError && <div style={{ fontSize: 13, color: '#C0392B', marginBottom: 10 }}>{saveError}</div>}

        {!locked && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button variant="primary" onClick={handleSave} disabled={!canSave}>
              {saving ? 'Saving…' : nomination ? '✓ Update nomination' : '✓ Submit nomination'}
            </Button>
            {savedAt && !saving && <span style={{ fontSize: 13, color: '#0F6E56' }}>Saved ✓</span>}
          </div>
        )}
        {!locked && nomination && (
          <p style={{ fontSize: 12, color: '#999', marginTop: 10, marginBottom: 0 }}>
            You can change your nomination any time until the deadline{deadline ? ` (${deadline})` : ''}.
          </p>
        )}
      </Card>
    </div>
  );
}
